import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    examApi,
    type Exam,
    type ExamFilters,
    type CreateExamData,
    type UpdateExamData,
} from "@/api/exam";

export const useExamStore = defineStore("exam", () => {
    // State
    const exams = ref<Exam[]>([]);
    const currentExam = ref<Exam | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const classId = ref("");
    const subjectId = ref("");
    const status = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeExams = computed(() =>
        exams.value.filter((exam) => exam.is_active),
    );

    const draftExams = computed(() =>
        exams.value.filter((exam) => exam.status === "draft"),
    );

    const scheduledExams = computed(() =>
        exams.value.filter((exam) => exam.status === "scheduled"),
    );

    const ongoingExams = computed(() =>
        exams.value.filter((exam) => exam.status === "ongoing"),
    );

    const closedExams = computed(() =>
        exams.value.filter((exam) => exam.status === "closed"),
    );

    const archivedExams = computed(() =>
        exams.value.filter((exam) => exam.status === "archived"),
    );

    const examsByStatus = computed(() => {
        const grouped: Record<string, Exam[]> = {
            draft: [],
            scheduled: [],
            ongoing: [],
            closed: [],
            archived: [],
        };

        exams.value.forEach((exam) => {
            if (exam.status && grouped[exam.status]) {
                grouped[exam.status].push(exam);
            }
        });

        return grouped;
    });

    // Actions
    const fetchExams = async (filters: ExamFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await examApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                class_id: classId.value || undefined,
                subject_id: subjectId.value || undefined,
                status:
                    (status.value as
                        | "draft"
                        | "scheduled"
                        | "ongoing"
                        | "closed"
                        | "archived"
                        | undefined) || undefined,
                ...filters,
            });

            exams.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch exams";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchExam = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentExam.value = await examApi.getById(id);
            return currentExam.value;
        } catch (err: any) {
            error.value = err.response?.data?.message || "Failed to fetch exam";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createExam = async (data: CreateExamData) => {
        loading.value = true;
        error.value = null;

        try {
            const newExam = await examApi.create(data);
            // Refresh list to ensure consistency
            await fetchExams();
            return newExam;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create exam";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateExam = async (id: string, data: UpdateExamData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedExam = await examApi.update(id, data);
            const index = exams.value.findIndex((e) => e.id === id);
            if (index !== -1) {
                exams.value[index] = updatedExam;
            }
            if (currentExam.value?.id === id) {
                currentExam.value = updatedExam;
            }
            return updatedExam;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update exam";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteExam = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await examApi.delete(id);
            exams.value = exams.value.filter((e) => e.id !== id);
            if (currentExam.value?.id === id) {
                currentExam.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete exam";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const publishExam = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedExam = await examApi.publish(id);
            const index = exams.value.findIndex((e) => e.id === id);
            if (index !== -1) {
                exams.value[index] = updatedExam;
            }
            if (currentExam.value?.id === id) {
                currentExam.value = updatedExam;
            }
            return updatedExam;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to publish exam";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const getExamStatistics = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const statistics = await examApi.getStatistics(id);
            return statistics;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch exam statistics";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateStatuses = async () => {
        loading.value = true;
        error.value = null;

        try {
            const result = await examApi.updateStatuses();
            // Refresh the list to show updated statuses
            await fetchExams();
            return result;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update exam statuses";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchActiveExams = async () => {
        loading.value = true;
        error.value = null;

        try {
            const activeExams = await examApi.getActive();
            return activeExams;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch active exams";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchScheduledExams = async () => {
        loading.value = true;
        error.value = null;

        try {
            const scheduledExams = await examApi.getScheduled();
            return scheduledExams;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch scheduled exams";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchEndedExams = async () => {
        loading.value = true;
        error.value = null;

        try {
            const endedExams = await examApi.getEnded();
            return endedExams;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch ended exams";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        classId.value = "";
        subjectId.value = "";
        status.value = "";
    };

    return {
        // State
        exams,
        currentExam,
        loading,
        error,
        page,
        perPage,
        search,
        classId,
        subjectId,
        status,
        meta,

        // Getters
        activeExams,
        draftExams,
        scheduledExams,
        ongoingExams,
        closedExams,
        archivedExams,
        examsByStatus,

        // Actions
        fetchExams,
        fetchExam,
        createExam,
        updateExam,
        deleteExam,
        publishExam,
        getExamStatistics,
        updateStatuses,
        fetchActiveExams,
        fetchScheduledExams,
        fetchEndedExams,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
